import React, {
  useContext,
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { IAsset, IAssetState, EMPTY_ASSET_FORM } from "../pages/Assets/core/models";
import {
  TAssetQrField,
  ASSET_QR_FIELD_KEYS,
  DEFAULT_ASSET_QR_FIELDS,
} from "../pages/Assets/core/qr";

// The chosen QR fields are a per-user labelling preference, not asset data, so
// they live in localStorage rather than on the record.
const QR_FIELDS_STORAGE_KEY = "sema.assets.qrFields";

function loadQrFields(): TAssetQrField[] {
  try {
    const raw = window.localStorage.getItem(QR_FIELDS_STORAGE_KEY);
    if (!raw) return [...DEFAULT_ASSET_QR_FIELDS];
    const parsed = JSON.parse(raw);
    // Drop anything that is no longer a known field (e.g. after a rename).
    const valid = Array.isArray(parsed)
      ? (parsed.filter((k) =>
          ASSET_QR_FIELD_KEYS.includes(k),
        ) as TAssetQrField[])
      : [];
    return valid.length > 0 ? valid : [...DEFAULT_ASSET_QR_FIELDS];
  } catch {
    return [...DEFAULT_ASSET_QR_FIELDS];
  }
}

interface AssetsContextType {
  asset: IAssetState;
  setAsset: Dispatch<SetStateAction<IAssetState>>;
  // The full record currently being acted on (needed for assignment history / QR).
  activeAsset: IAsset | null;
  setActiveAsset: Dispatch<SetStateAction<IAsset | null>>;
  // Which asset details get encoded into generated QR codes.
  qrFields: TAssetQrField[];
  setQrFields: Dispatch<SetStateAction<TAssetQrField[]>>;
}

const AssetsContext = createContext<AssetsContextType | null>(null);

const AssetsProvider = ({ children }: { children: React.ReactNode }) => {
  const [asset, setAsset] = useState<IAssetState>({
    action: "create",
    data: { ...EMPTY_ASSET_FORM },
  });
  const [activeAsset, setActiveAsset] = useState<IAsset | null>(null);
  const [qrFields, setQrFields] = useState<TAssetQrField[]>(loadQrFields);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        QR_FIELDS_STORAGE_KEY,
        JSON.stringify(qrFields),
      );
    } catch {
      // Private-mode / quota failures shouldn't break QR generation.
    }
  }, [qrFields]);

  return (
    <AssetsContext.Provider
      value={{
        asset,
        setAsset,
        activeAsset,
        setActiveAsset,
        qrFields,
        setQrFields,
      }}
    >
      {children}
    </AssetsContext.Provider>
  );
};

function useAssetsPage() {
  const context = useContext(AssetsContext);
  if (!context) {
    throw new Error("useAssetsPage must be used within an AssetsProvider");
  }
  return context;
}

export { AssetsProvider, useAssetsPage };
